import FilterListIcon from '@mui/icons-material/FilterList';
import {
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import type { KandidaatOverzicht } from '../../api/typen.ts';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { datum, ofStreepje, opsomming, volledigeNaam } from '../../helpers/formatteer.ts';
import type { Eigenschap, Sorteerrichting, Sorteerveld } from './kandidaatEigenschappen.ts';

interface Props {
  kandidaten: KandidaatOverzicht[];
  kolommen: readonly Eigenschap[];
  sortering: { veld: Sorteerveld; richting: Sorteerrichting };
  gefilterd: ReadonlySet<Sorteerveld>;
  onSorteer: (veld: Sorteerveld) => void;
  onFilter: (eigenschap: Eigenschap, anker: HTMLElement) => void;
}

/**
 * De tabel met kandidaten. Een klik op een kolomkop sorteert; het filtericoon in de kop opent het filter.
 * @param props De kandidaten, de zichtbare kolommen, de sortering, de gefilterde eigenschappen en de acties.
 * @returns De tabel.
 */
export function KandidatenTabel({ kandidaten, kolommen, sortering, gefilterd, onSorteer, onFilter }: Props) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {kolommen.map((eigenschap) => {
              const actief = sortering.veld === eigenschap.sleutel;
              const richting = actief && sortering.richting === 'aflopend' ? 'desc' : 'asc';
              const label = eigenschap.label.toLowerCase();
              return (
                <TableCell
                  key={eigenschap.sleutel}
                  sortDirection={actief ? richting : false}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <Stack direction="row" sx={{ alignItems: 'center' }}>
                    <TableSortLabel active={actief} direction={richting} onClick={() => onSorteer(eigenschap.sleutel)}>
                      {eigenschap.label}
                    </TableSortLabel>
                    {eigenschap.filter && (
                      <Tooltip title={`Filter op ${label}`}>
                        <IconButton
                          size="small"
                          aria-label={`Filter op ${label}`}
                          color={gefilterd.has(eigenschap.sleutel) ? 'primary' : 'default'}
                          onClick={(gebeurtenis) => onFilter(eigenschap, gebeurtenis.currentTarget)}
                          sx={{ ml: 0.5, opacity: gefilterd.has(eigenschap.sleutel) ? 1 : 0.5 }}
                        >
                          <FilterListIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {kandidaten.length === 0 ? (
            <TableRow>
              <TableCell colSpan={kolommen.length} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                Geen kandidaten gevonden.
              </TableCell>
            </TableRow>
          ) : (
            kandidaten.map((kandidaat) => (
              <TableRow key={kandidaat.id} hover>
                {kolommen.map((eigenschap) => (
                  <TableCell key={eigenschap.sleutel}>{cel(eigenschap.sleutel, kandidaat)}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function cel(sleutel: Sorteerveld, kandidaat: KandidaatOverzicht): ReactNode {
  switch (sleutel) {
    case 'naam':
      return (
        <Link
          component={RouterLink}
          to={`/kandidaten/${kandidaat.id}`}
          underline="hover"
          sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          {volledigeNaam(kandidaat)}
        </Link>
      );
    case 'status':
      return <StatusChip soort="kandidaat" waarde={kandidaat.status} />;
    case 'linkedinUrl':
      return kandidaat.linkedinUrl ? (
        <Link href={kandidaat.linkedinUrl} target="_blank" rel="noopener noreferrer" underline="hover">
          {kandidaat.linkedinUrl.replace(/^https?:\/\/(www\.)?/, '')}
        </Link>
      ) : (
        '–'
      );
    case 'geboortedatum':
    case 'aangemaaktOp':
    case 'gewijzigdOp':
      return datum(kandidaat[sleutel]);
    case 'woonplaats':
      return ofStreepje(kandidaat.locatie?.plaats);
    case 'rijbewijzen':
      return opsomming(kandidaat.rijbewijzen);
    case 'talentpools':
    case 'functietitels':
      return opsomming(kandidaat[sleutel].map((item) => item.naam));
    default:
      return ofStreepje(kandidaat[sleutel]);
  }
}
